/**
 * 分镜卡片（v3 - 双模式视觉差异化）
 *
 * 模式分类（来自 shot.mode）：
 *  - keyframe：单首帧 → 视频（sky 系，封面=锁定关键帧，hover 播视频）
 *  - frame_sequence：多帧 → 视频（amber 系，封面=首帧，徽标显示帧数）
 *  - trinity_cards：三合一卡 → 视频（violet 系，封面=首张完成卡，徽标显示完成数）
 *
 * 设计目标：
 * - 一卡只有一块 16:9 媒体区，hover 视频自动切播（仅 keyframe 完成态）
 * - 顶部：镜头编号 SHOT-XX + 时长（keyframe）/ 帧数（frame_sequence）
 * - 底部：标题 + prompt 预览 + 状态胶囊
 * - 状态色：草稿（虚边）/ 已锁定/有帧（sky/amber）/ 进行中（amber 微脉冲）/ 完成（emerald）/ 失败（rose）
 */

import { useRef, useState } from 'react';
import {
  AlertCircle,
  Copy,
  MoreHorizontal,
  Play,
  Trash2,
  ChevronUp,
  ChevronDown,
  ImageOff,
  Sparkles,
  Repeat,
  Clock,
  Film,
  CheckCircle2,
  Loader2,
  Layers,
  GripVertical,
} from 'lucide-react';
import type { DirectorShot } from '../../services/directorApi';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

type VideoStatus =
  | 'none'
  | 'queued'
  | 'pending'
  | 'submitting'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'timeout';

type ShotMode = 'keyframe' | 'frame_sequence' | 'trinity_cards';

interface ShotCardProps {
  shot: DirectorShot;
  index: number;
  selected: boolean;
  /** 锁定 keyframe 的缩略图 URL（keyframe 模式专用） */
  keyframeUrl?: string | null;
  /** 帧序列首帧 URL（frame_sequence 模式专用，作封面） */
  firstFrameUrl?: string | null;
  /** 帧序列长度（frame_sequence 模式专用） */
  frameCount?: number;
  /** 三合一卡封面（trinity_cards 模式专用） */
  trinityCoverUrl?: string | null;
  /** 已完成三合一卡数量（trinity_cards 模式专用） */
  trinityCardCount?: number;
  /** 视频状态文本 */
  videoStatus?: VideoStatus;
  /** 视频成片 URL（用于 hover 自动播放） */
  videoUrl?: string | null;
  /**
   * 已激活视频的模型人话名（来自 VideoJob.model 快照）。
   * 仅在 videoStatus='completed' + videoUrl 存在时显示，浮在媒体区右下方做小徽标。
   */
  videoModelName?: string | null;
  onSelect: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDuplicate?: () => void;
  onReuseKeyframe?: () => void;
  onDelete?: () => void;
  /** ---- 拖拽重排（故事板）---- */
  /** 是否可拖拽（项目非 composing/archived 时为 true） */
  draggable?: boolean;
  /** 拖拽态视觉：本卡正在被拖 */
  isDragging?: boolean;
  /** 拖拽态视觉：拖拽悬停在本卡上方（显示插入指示） */
  isDragOver?: boolean;
  onDragStart?: () => void;
  onDragEnter?: () => void;
  onDragEnd?: () => void;
  onDrop?: () => void;
}

/** 状态 → 颜色 token，按分镜模式区分 */
function statusVisual(
  mode: ShotMode,
  hasContent: boolean,
  status: VideoStatus,
  trinityCardCount: number
) {
  // 草稿：未锁定关键帧 / 未添加帧
  if (!hasContent) {
    return {
      ring: 'ring-1 ring-dashed ring-slate-300/80',
      pill: 'bg-slate-100 text-slate-500',
      label:
        mode === 'frame_sequence'
          ? '未添加帧'
          : mode === 'trinity_cards'
            ? '未生成卡片'
            : '草稿',
      icon: <Sparkles className="h-3 w-3" />,
    };
  }
  switch (status) {
    case 'completed':
      return {
        ring: 'ring-1 ring-emerald-200',
        pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100',
        label: '已激活',
        icon: <CheckCircle2 className="h-3 w-3" />,
      };
    case 'failed':
    case 'timeout':
      return {
        ring: 'ring-1 ring-rose-200',
        pill: 'bg-rose-50 text-rose-600 ring-1 ring-inset ring-rose-100',
        label: status === 'timeout' ? '超时' : '失败',
        icon: <AlertCircle className="h-3 w-3" />,
      };
    case 'queued':
      return {
        ring: 'ring-1 ring-sky-200',
        pill: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-100',
        label: '排队中',
        icon: <Clock className="h-3 w-3" />,
      };
    case 'pending':
    case 'submitting':
    case 'processing':
      return {
        ring: 'ring-1 ring-amber-200',
        pill: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-100',
        label: '生成中',
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
      };
    default:
      // 已就绪、未激活：模式色
      return mode === 'frame_sequence'
        ? {
            ring: 'ring-1 ring-amber-200',
            pill: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-100',
            label: '帧已就绪',
            icon: <Layers className="h-3 w-3" />,
          }
        : mode === 'trinity_cards'
          ? {
              ring: 'ring-1 ring-violet-200',
              pill: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-100',
              label: trinityCardCount >= 3 ? '三卡已就绪' : `${trinityCardCount}/3 卡`,
              icon: <Sparkles className="h-3 w-3" />,
            }
        : {
            ring: 'ring-1 ring-sky-200',
            pill: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-100',
            label: '已锁定',
            icon: <Film className="h-3 w-3" />,
          };
  }
}

export function ShotCard({
  shot,
  index,
  selected,
  keyframeUrl,
  firstFrameUrl,
  frameCount = 0,
  trinityCoverUrl,
  trinityCardCount = 0,
  videoStatus = 'none',
  videoUrl,
  videoModelName,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onReuseKeyframe,
  onDelete,
  draggable = false,
  isDragging = false,
  isDragOver = false,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
}: ShotCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  /** 拖拽手柄按下时才允许 draggable，避免卡片内按钮 / 文本选择触发拖拽 */
  const [dragArmed, setDragArmed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // 用 shot.mode 而非项目级 mode
  const isFrameSequence = shot.mode === 'frame_sequence';
  const isTrinityMode = shot.mode === 'trinity_cards';

  // 内容判断：keyframe 模式看 keyframe，frame_sequence 看帧数，trinity 看已完成卡数
  const hasContent = isFrameSequence
    ? frameCount > 0
    : isTrinityMode
      ? trinityCardCount > 0
      : !!shot.lockedKeyframeImageJobId;

  // 封面图：keyframe 用 keyframeUrl；frame_sequence 用 firstFrameUrl；trinity 用首张完成卡
  const coverUrl = isFrameSequence
    ? firstFrameUrl ?? null
    : isTrinityMode
      ? trinityCoverUrl ?? null
      : keyframeUrl ?? null;

  const visual = statusVisual(shot.mode, hasContent, videoStatus, trinityCardCount);
  const canPlayInline = videoStatus === 'completed' && !!videoUrl;

  const handleMouseEnter = () => {
    setHovering(true);
    if (canPlayInline && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => undefined);
    }
  };
  const handleMouseLeave = () => {
    setHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // 选中态描边色按模式切换
  const selectedClass = selected
    ? isFrameSequence
      ? 'ring-2 ring-amber-500 shadow-[0_22px_60px_rgba(245,158,11,0.28)] -translate-y-0.5'
      : isTrinityMode
        ? 'ring-2 ring-violet-500 shadow-[0_22px_60px_rgba(139,92,246,0.28)] -translate-y-0.5'
      : 'ring-2 ring-sky-500 shadow-[0_22px_60px_rgba(56,189,248,0.28)] -translate-y-0.5'
    : 'hover:-translate-y-0.5 hover:shadow-[0_24px_60px_rgba(15,23,42,0.12)]';

  // 镜头编号补零
  const shotNo = String(index + 1).padStart(2, '0');

  return (
    <div
      onClick={onSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-shot-id={shot.id}
      draggable={draggable && dragArmed}
      onDragStart={(e) => {
        if (!draggable || !dragArmed) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = 'move';
        // Firefox 需要 setData 才能启动拖拽
        try {
          e.dataTransfer.setData('text/plain', shot.id);
        } catch {
          // 忽略
        }
        onDragStart?.();
      }}
      onDragEnter={(e) => {
        if (!draggable) return;
        e.preventDefault();
        onDragEnter?.();
      }}
      onDragOver={(e) => {
        // 必须 preventDefault 才能触发 onDrop
        if (draggable) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!draggable) return;
        e.preventDefault();
        onDrop?.();
        setDragArmed(false);
      }}
      onDragEnd={() => {
        onDragEnd?.();
        setDragArmed(false);
      }}
      className={`group relative flex shrink-0 w-[260px] cursor-pointer flex-col overflow-hidden rounded-[22px] border border-white/80 bg-white/95 backdrop-blur-xl transition-all duration-200 ${visual.ring} ${selectedClass} ${
        isDragging ? 'opacity-40' : ''
      } ${isDragOver ? 'ring-2 ring-sky-400 ring-offset-2 ring-offset-white' : ''}`}
    >
      {/* 拖拽手柄（仅 draggable 时显示，hover 卡片时浮现） */}
      {draggable && (
        <div
          onMouseDown={() => setDragArmed(true)}
          onMouseUp={() => setDragArmed(false)}
          onClick={(e) => e.stopPropagation()}
          className="absolute left-1/2 top-1 z-30 -translate-x-1/2 cursor-grab rounded-full bg-black/45 px-2 py-0.5 text-white/90 opacity-0 backdrop-blur-sm transition active:cursor-grabbing group-hover:opacity-100"
          title="拖动调整顺序"
        >
          <GripVertical className="h-3.5 w-3.5 rotate-90" />
        </div>
      )}
      {/* 媒体区：单块 16:9 */}
      <div
        className={`relative aspect-video overflow-hidden ${
          isFrameSequence
            ? 'bg-gradient-to-br from-amber-50 via-orange-50/50 to-amber-100/40'
            : isTrinityMode
              ? 'bg-gradient-to-br from-violet-50 via-fuchsia-50/50 to-violet-100/40'
            : 'bg-gradient-to-br from-slate-100 via-slate-50 to-sky-50'
        }`}
      >
        {/* 封面 */}
        {coverUrl ? (
          <img
            src={resolveBrowserMediaUrl(coverUrl)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
            {isFrameSequence ? (
              <>
                <Layers className="h-5 w-5 text-amber-300" />
                <span className="text-[11px]">未添加帧</span>
              </>
            ) : isTrinityMode ? (
              <>
                <Sparkles className="h-5 w-5 text-violet-300" />
                <span className="text-[11px]">未生成卡片</span>
              </>
            ) : (
              <>
                <ImageOff className="h-5 w-5" />
                <span className="text-[11px]">未锁定关键帧</span>
              </>
            )}
          </div>
        )}

        {/* hover 时叠加播放视频 */}
        {canPlayInline && (
          <video
            ref={videoRef}
            src={videoUrl ? resolveBrowserMediaUrl(videoUrl) : undefined}
            muted
            loop
            playsInline
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ${hovering ? 'opacity-100' : 'opacity-0'}`}
          />
        )}

        {/* 顶部渐变压暗，承载序号与状态 */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/55 via-black/20 to-transparent" />

        {/* 左上：镜头编号 + (时长 / 帧数) */}
        <div className="absolute left-2.5 top-2.5 z-10 flex items-center gap-1.5">
          <span className="rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.18em] text-white/95 backdrop-blur-sm">
            SHOT-{shotNo}
          </span>
          {isFrameSequence ? (
            frameCount > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/85 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm">
                <Layers className="h-3 w-3" />
                {frameCount} 帧
              </span>
            ) : null
          ) : isTrinityMode ? (
            trinityCardCount > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-violet-500/85 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm">
                <Sparkles className="h-3 w-3" />
                {trinityCardCount}/3 卡
              </span>
            ) : null
          ) : shot.duration ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-black/45 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
              <Clock className="h-3 w-3" />
              {shot.duration}s
            </span>
          ) : null}
        </div>

        {/* 右上：菜单按钮 */}
        <div className="absolute right-2 top-2 z-20" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-full bg-white/90 p-1.5 text-slate-700 shadow-sm transition hover:bg-white"
            aria-label="分镜菜单"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 z-30 min-w-[148px] overflow-hidden rounded-2xl border border-slate-100 bg-white py-1 text-sm shadow-xl shadow-slate-300/50">
              {onMoveUp && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onMoveUp();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                >
                  <ChevronUp className="h-4 w-4" /> 上移
                </button>
              )}
              {onMoveDown && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onMoveDown();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                >
                  <ChevronDown className="h-4 w-4" /> 下移
                </button>
              )}
              {onDuplicate && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDuplicate();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                >
                  <Copy className="h-4 w-4" /> 复制
                </button>
              )}
              {onReuseKeyframe && shot.mode === 'keyframe' && shot.lockedKeyframeImageJobId && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onReuseKeyframe();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-slate-700 transition hover:bg-sky-50 hover:text-sky-700"
                  title="新分镜复用本镜 keyframe，prompt 留空"
                >
                  <Repeat className="h-4 w-4" /> 复用关键帧
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-rose-600 transition hover:bg-rose-50"
                >
                  <Trash2 className="h-4 w-4" /> 删除
                </button>
              )}
            </div>
          )}
        </div>

        {/* 右下：状态胶囊（叠加：模型名在上，状态在下） */}
        <div className="absolute bottom-2 right-2 z-10 flex flex-col items-end gap-1">
          {canPlayInline && videoModelName && (
            <span
              className="inline-block max-w-[140px] truncate rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white/95 backdrop-blur-sm"
              title={`激活时的视频模型：${videoModelName}`}
            >
              {videoModelName}
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold backdrop-blur-sm ${visual.pill}`}
          >
            {visual.icon}
            {visual.label}
          </span>
        </div>

        {/* 左下：上下文过期提示 */}
        {shot.contextStaleHint && (
          <div className="absolute bottom-2 left-2 z-10">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-100"
              title="上下文已变更，建议重新激活"
            >
              <AlertCircle className="h-3 w-3" />
              已过期
            </span>
          </div>
        )}

        {/* 已激活成片：中央播放图标（hover 时浅化让位视频） */}
        {canPlayInline && !hovering && (
          <div className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/85 text-slate-900 shadow-md backdrop-blur-sm transition group-hover:scale-110">
              <Play className="h-4 w-4 translate-x-[1px]" fill="currentColor" />
            </span>
          </div>
        )}

        {/* frame_sequence 模式：左下角胶片层叠装饰，强化"多帧"语义 */}
        {isFrameSequence && hasContent && (
          <div className="pointer-events-none absolute bottom-2 left-2 z-[6] flex items-center gap-0.5">
            {[0, 1, 2].slice(0, Math.min(frameCount, 3)).map((i) => (
              <span
                key={i}
                className="h-1.5 w-3 rounded-sm bg-white/85 shadow-sm"
                style={{ opacity: 1 - i * 0.25 }}
              />
            ))}
          </div>
        )}
      </div>

      {/* 文本区 */}
      <div className="flex flex-1 flex-col gap-1 px-3 py-2.5">
        <div className="flex items-baseline gap-1.5">
          <span
            className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
              isFrameSequence
                ? 'text-amber-500'
                : isTrinityMode
                  ? 'text-violet-500'
                  : 'text-slate-400'
            }`}
          >
            #{index + 1}
          </span>
          <span className="line-clamp-1 flex-1 text-[13px] font-semibold text-slate-900">
            {shot.title || '未命名分镜'}
          </span>
        </div>
        <div className="line-clamp-2 min-h-[2.5rem] text-[11px] leading-[1.25rem] text-slate-500">
          {shot.prompt || (
            <span className="italic text-slate-400">点击编辑 prompt…</span>
          )}
        </div>
      </div>
    </div>
  );
}
